import {Page} from "../page"

export const replayBadges: Page = {
    uri: 'learn/replay-badges',
    icon: '🏆',
    title: $localize`:@@page-replay-badges-title:Replay & Badges`,
    seoTitle: $localize`:@@page-replay-badges-seo-title:Ludoya Replay and badges — monthly recaps and milestones`,
    summary: $localize`:@@page-replay-badges-summary:Your gaming life, celebrated — an animated recap of every period, and badges for every milestone.`,
    markdownContent: [
        $localize`:@@page-replay-badges-intro:Logging plays isn't just record-keeping — Ludoya turns your history into things worth sharing. **Ludoya Replay** is an animated recap of your gaming period, and **badges** mark the milestones you hit along the way.`,

        $localize`:@@page-replay-badges-replay:## Ludoya Replay

Ludoya Replay is an animated, story-style carousel that recaps a period of your gaming life — swipe through it card by card, like a year-in-review for board games.

![The opening card of a monthly Replay](/assets/screenshots/replay-carousel.webp)

### What's Inside

- **Activity** — how much you played: sessions, hours, and your rhythm over the period
- **Top day** — your biggest gaming day, with everything you played and everyone who was there
- **Top games** — the titles that defined your period
- **Discoveries** — new games tried and new people played with for the first time
- **Collection** — what entered your shelves
- **People** — who you shared the table with most
- **Comparison** — how the period stacks up against the one before
- **Featured plays** — standout sessions picked automatically: your longest game, your fullest table, a nail-biting close win, or the photo everyone liked

### When Replays Arrive

A fresh **monthly Replay** is ready on the 1st, recapping the month just gone, and you get a notification when it lands. The **yearly Replay** appears the same way on 1 January, once the year it covers has actually finished — your full year at the table.

A period needs a handful of plays before it earns a Replay, so a very quiet month or year won't produce one. Everything that qualifies stays browsable, so you can revisit any past month or year whenever you like.

### Group and Event Replays

[Groups](/learn/groups) get their own Replay recapping the whole community's activity, and [events](/learn/events) can end with an Event Replay — a shareable recap of everything played there. Great for closing out a convention weekend.

### Sharing

Any Replay card can be captured as an image and shared — to your feed, your group chat, or social media.`,

        $localize`:@@page-replay-badges-badges:## Badges

![A badge's detail: its rarity, your progress to the next level, and every tier with its threshold](/assets/screenshots/badges-catalog.webp)

Badges celebrate what you actually do on Ludoya — playing, collecting, hosting, teaching, trading. There are over 35 of them across categories like Collection, Plays, Community, Mentorship, Events, Campaigns, Game Kits, AI Assistant, Marketplace, Data Quality, and Special.

A taste of the roster:

- **Chronicler** — total plays logged
- **Explorer** — distinct games played
- **Hoarder** — games owned in your collection
- **Lone Wolf / Dynamic Duo / Power Trio / Party Animal** — plays at each table size
- **Guru** — plays where you taught someone a new game
- **Host with the Most** — events organized
- **Nomad** — distinct places you've played at
- **Well-Rounded** — your game H-index: N games played at least N times each

### Levels and Rarities

Most badges are **tiered** — keep doing the thing and the badge levels up. Every earned level has a rarity: **Common**, **Uncommon**, **Rare**, or **Legendary**. Some badges are one-off **special** unlocks (like playing in all four seasons of a year, or being among the first 1,000 players to log a play), and the **Anniversary** badge gains a level for every year you're on Ludoya.

### Earning and Showing Them Off

Unlocks pop up as an animated celebration with a rarity glow the moment you qualify, and each badge links back to the play, event, or post that earned it. Pick up to **3 favorites** for the showcase at the top of your [profile](/learn/user-profiles), and browse anyone's full badge collection from their profile.

### Club Badges

Clubs can design badges of their own ([see the plans](/premium)): one for every member and one for each public [membership tier](/learn/membership-tiers), with a name, a symbol or the club's logo, and a colour. You wear them for as long as you're a member, or on that tier. They sit in a **Clubs** row of their own, apart from the achievements above, and you can wear one of a public club's badges **On your picture**, as a crest on your avatar wherever it shows.

![A player's earned badges, leveled and grouped by category](/assets/screenshots/replay-badges-page.webp)`,

        $localize`:@@page-replay-badges-related:## Related

- [Statistics](/learn/statistics) — the full numbers behind your Replay
- [Play Logging](/learn/play-logging) — where all of this data comes from
- [User Profiles](/learn/user-profiles) — your showcase, badges, and public stats
- [Social Feed](/learn/social-feeds) — share Replay cards and badge unlocks`,
    ].join('\n\n---\n\n')
}
